import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { X, Play, Clock, CheckCircle } from 'lucide-react';

interface DemoVideoModalProps {
    isOpen: boolean;
    onClose: () => void;
    videoSrc?: string; // When your real demo video is ready, pass its URL here
}

const DemoVideoModal: React.FC<DemoVideoModalProps> = ({
    isOpen,
    onClose,
    videoSrc,
}) => {
    const { t } = useLanguage();

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[#070910]/80 backdrop-blur-md animate-in fade-in duration-300"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-[#0D111C] rounded-3xl shadow-2xl w-full max-w-5xl overflow-hidden transform transition-all animate-in zoom-in-95 duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/20">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                            {t('landing.view_demo', 'View Demo')}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-colors text-slate-500 dark:text-slate-400"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Video Player Area or Coming Soon */}
                {videoSrc ? (
                    <div className="aspect-video bg-black relative flex items-center justify-center">
                        <video
                            controls
                            autoPlay
                            className="w-full h-full object-contain"
                            src={videoSrc}
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                ) : (
                    <div
                        className="aspect-video relative flex flex-col items-center justify-center overflow-hidden"
                        style={{
                            background: 'linear-gradient(135deg, #0D111C 0%, #070910 100%)',
                        }}
                    >
                        {/* Animated background grid */}
                        <div
                            className="absolute inset-0 opacity-10"
                            style={{
                                backgroundImage:
                                    'linear-gradient(rgba(251,146,60,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.4) 1px, transparent 1px)',
                                backgroundSize: '40px 40px',
                            }}
                        />

                        {/* Glowing orb */}
                        <div
                            className="absolute w-72 h-72 rounded-full opacity-20"
                            style={{
                                background: 'radial-gradient(circle, #f97316 0%, transparent 70%)',
                                filter: 'blur(40px)',
                            }}
                        />

                        {/* Play button ring */}
                        <div className="relative mb-6">
                            <div className="w-24 h-24 rounded-full border-2 border-orange-500/40 flex items-center justify-center animate-pulse">
                                <div className="w-16 h-16 rounded-full bg-orange-500/20 border border-orange-500/60 flex items-center justify-center backdrop-blur-sm">
                                    <Play className="w-7 h-7 text-orange-400 ml-1" fill="currentColor" />
                                </div>
                            </div>
                        </div>

                        {/* Text */}
                        <div className="relative text-center px-8 z-10">
                            <div className="flex items-center justify-center gap-2 mb-3">
                                <Clock className="w-4 h-4 text-orange-400" />
                                <span className="text-orange-400 text-sm font-semibold uppercase tracking-widest">
                                    Coming Soon
                                </span>
                            </div>
                            <h2 className="text-white text-2xl font-bold mb-2">
                                Full Demo Video
                            </h2>
                            <p className="text-slate-400 text-sm max-w-md leading-relaxed">
                                Our professional walkthrough video is being produced. In the meantime, start a free trial to explore the full power of Lashing Calculator.
                            </p>
                        </div>

                        {/* Feature list */}
                        <div className="relative mt-6 flex flex-wrap gap-3 justify-center z-10">
                            {['Smart Lashing Engine', '3D Visualization', 'PDF Reports', 'IMO Compliant'].map((feat) => (
                                <div
                                    key={feat}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-slate-300"
                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                                >
                                    <CheckCircle className="w-3 h-3 text-orange-400" />
                                    {feat}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer Info */}
                <div className="px-6 py-4 bg-slate-50 dark:bg-[#070910]/40 text-center border-t dark:border-white/10">
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                        Lashing Calculator v3.0 — Professional Cargo Securing &amp; 3D Visualization
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DemoVideoModal;
